import { Grid, Tab } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  OTDetalleInstallTecnicoFormTab,
  OTDetalleClienteInstallTecnicoFormTab,
  OTInstalacionTecnicoFormTab,
} from '@/erp/tecnico/orden-trabajo/shared/components';
import { a11yProps, EstadosOrdenTrabajoEnumChoice, texto, useTabsFormOnly } from '@/shared';
import {
  CustomTabPanel,
  FormTabsOnly,
  SingleButton,
  TabsFormBoxScene,
} from '@/shared/components/ui';
import {
  Agendamiento,
  IDataPreventa,
  IDataProspecto,
  Naps,
  OrdenTrabajo,
} from '@/shared/interfaces';
import { returlUrlAuditoriaInstalacionesPage } from '../../../pages/tables/AuditoriaInstalacionesOTMainPage/AuditoriaInstalacionesOTMainPage';
import { AuditoriaOTInstallDocsFormTab } from './docs';
import { EquiposMaterialesViewOTInstallFormTab } from './equipos';
import RejectToUpdateInstalacionAsigTecModal from '@/erp/tecnico/orden-trabajo/shared/components/rejections/RejectToUpdateInstalacionAsigTecModal';
import { useUiConfirmModalStore } from '@/store/ui';
import { useUpdateOrdenTrabajo } from '@/shared/actions/erp';
import { useAuthStore } from '@/store/auth';
import { CustomAlertCard } from '@/shared/components/ui/Alerts';
import dayjs from 'dayjs';

export type SaveAuditoriaInstallOTProps = {
  title: string;
  ordentrabajo?: OrdenTrabajo;
};

type AuditoriaOTForm = Partial<OrdenTrabajo> &
  Partial<IDataProspecto> &
  Partial<IDataPreventa> &
  Partial<Agendamiento> & {
    // auxiliar fields
    flotaLabel?: string;
    planLabel?: string;
    fecha_hora_instalacion?: string;

    observacion_tecnico?: string;
    observacion_activacion?: string;
    forma_pago?: string;

    observacion_1?: string;
    observacion_2?: string;
    observacion_llamada?: string;
    incidencia?: string;

    metraje_autorizado?: string;

    es_conexion_tierra: boolean;
    es_regulador: boolean;

    rawNap?: Naps;
  };

const SaveAuditoriaInstallOT: React.FC<SaveAuditoriaInstallOTProps> = ({
  title,
  ordentrabajo,
}) => {

  // ===========================================================================
  // CONSTANTS
  // ===========================================================================
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  const currentUser = useAuthStore(s=>s.user)?.username
  const auxAuditoria = {
    aprobado: false,
    fecha_aprobado: "",
    historial_auditoria: [],
    user_aprueba: "",
  }
  
  ///* hooks -------------------------
  const navigate = useNavigate();
  const { handleMethodChange, valueTab } = useTabsFormOnly({
    initialTabValue: 1,
  });
  const [openRejectModal, setOpenRejectModal] = useState(false);
  // const [openAproveFormulario, setOpenAproveFormulario] = useState<boolean>(false);

  ///* form -------------------------
  const form = useForm<AuditoriaOTForm>({});
  const { errors } = form.formState;

  // ===========================================================================
  // MUTATIONS
  // ===========================================================================
  const { mutateAsync:putOrdenTrabajo } = useUpdateOrdenTrabajo<Partial<OrdenTrabajo>>({
    navigate,
    returnUrl:returlUrlAuditoriaInstalacionesPage
  })


  ///* effects -------------------------
  useEffect(() => {
    if (!ordentrabajo?.id_orden_trabajo) return;

    const { prospecto_data, preventa_data, agendamiento_data, ...rest } =
      ordentrabajo;
    delete rest?.contrato_data;

    ///* reset form -------------
    form.reset({
      ...prospecto_data,
      ...preventa_data,
      ...agendamiento_data,
      ...rest,
      planLabel: ordentrabajo?.plan_data?.nombre || '',
      flotaLabel: ordentrabajo?.flota_data?.nombre || '',
      tipo_instalacion: preventa_data?.tipo_instalacion || '',
      valor_instalacion: preventa_data?.valor_instalacion || '',
      observacion_activacion:
        ordentrabajo?.observaciones?.observacion_activacion,

      observacion_1: ordentrabajo?.preventa_data?.observaciones?.observacion_1,
      observacion_2: ordentrabajo?.preventa_data?.observaciones?.observacion_2,
      observacion_llamada:
        agendamiento_data?.observaciones?.observacion_llamada,

      metraje_autorizado: rest?.ciudad_data?.metraje_autorizado || '0',

      es_conexion_tierra: !!rest?.es_conexion_tierra,
      es_regulador: !!rest?.es_regulador,

      ///* Activacion ---
      rawNap: rest.nap_data,

      ///* Auditoria ---
      incidencia: rest?.incidencia || '',
      prioridad_incidencia: rest?.prioridad_incidencia || '',
      observacion_tecnico: rest?.observaciones?.observacion_tecnico || '',
    } as unknown as AuditoriaOTForm);
  }, [ordentrabajo]);

  return (
    <TabsFormBoxScene
      titlePage={title}
      showBtns={false}
      // // custom btns ---------
      showCustomBtns={true}
      customBtns={
        <Grid
          item
          container
          spacing={2}
          gap={1}
          xs={12}
          sx={{
            mt: 5,
          }}
        >
          
          <span className="spacer"></span>

          <SingleButton
            label="REGRESAR"
            variant="text"
            showIcon={false}
            onClick={() => {
              navigate(returlUrlAuditoriaInstalacionesPage);
            }}
          />
          {
            (ordentrabajo?.estado_orden_trabajo === EstadosOrdenTrabajoEnumChoice.REALIZADA 
            || ordentrabajo?.estado_orden_trabajo === EstadosOrdenTrabajoEnumChoice.REALIZADA_ACTUALIZADA) && (
              <>
                <SingleButton
                  label="SOLICITAR ACTUALIZACIÓN"
                  variant="outlined"
                  showIcon={false}
                  onClick={() => {
                    setOpenRejectModal(true);
                  }}
                />
                <SingleButton
                  label="APROBAR"
                  variant="contained"
                  showIcon={false}
                  onClick={() => {
                    // setOpenAproveFormulario(true);
                    setConfirmDialog({
                      isOpen: true,
                      title: "Aprobar Orden de Trabajo",
                      subtitle:
                        "¿Esta seguro que la orden de trabajo se encuentra completa y correcta?",
                      onConfirm: () => {
                        
                        auxAuditoria.user_aprueba=currentUser!
                        auxAuditoria.aprobado=true
                        auxAuditoria.fecha_aprobado=new Date().toISOString()
                        if(ordentrabajo?.resumen_auditoria){
                          auxAuditoria.historial_auditoria=ordentrabajo?.resumen_auditoria?.historial_auditoria as any
                        }
                        putOrdenTrabajo({
                          id: ordentrabajo?.id_orden_trabajo!,
                          data: {
                            estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.REALIZADA_APROBADA,
                            resumen_auditoria: auxAuditoria
                          }
                        })
                        setConfirmDialogIsOpen(false)
                        // setOpenAproveFormulario(false);
                      },
                      onClose: () => {
                        setConfirmDialogIsOpen(false)
                      },
                      confirmTextBtn: "SI",
                      cancelTextBtn: "NO",
                    });
                  }}
                />
              </>
            )
          }
        </Grid>
      }
      tabs={<>
        {
          form.getValues()?.observaciones?.observacion_actualizacion_tecnico
          && (
            form.getValues()?.estado_orden_trabajo === EstadosOrdenTrabajoEnumChoice.ESPERA_ACTUALIZAR
            || form.getValues()?.estado_orden_trabajo === EstadosOrdenTrabajoEnumChoice.REALIZADA_ACTUALIZADA
          )?
          <Grid container spacing={2} justifyContent={"center"} my={2}>
            <CustomAlertCard
              alertTitle="Actualización solicitada por Auditoría"
              alertMessage={`${texto.correccion_solicitada_tecnico_auditoria} \n ${form.getValues()?.motivo_rechazo_data?.nombre}: ${form.getValues()?.observaciones?.observacion_actualizacion_tecnico} (${dayjs(form.getValues()?.observaciones?.fecha_solicitada_actualizacion_tecnico).format("DD/MM/YYYY HH:mm:ss")||"Fecha no definida"})`}
              bgColorAlert={"SKY"} />
          </Grid>
          :null
        }
        <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
          <Tab label="ORDEN TRABAJO" value={1} {...a11yProps(1)} />
          <Tab label="DETALLE INSTALACIÓN" value={2} {...a11yProps(2)} />
          <Tab label="INFORMACIÓN CLIENTE" value={3} {...a11yProps(3)} />
          <Tab label="MATERIALES" value={4} {...a11yProps(4)} />

          <Tab label="DOCUMENTOS" value={5} {...a11yProps(5)} />
        </FormTabsOnly>
      </>
      }
    >
      {/* ===================== ORDEN TRABAJO ===================== */}
      <CustomTabPanel index={valueTab} value={1}>
        <OTInstalacionTecnicoFormTab
          form={form}
          errors={errors}
          shrink={true}
          viewContratos={true}
        />
      </CustomTabPanel>

      {/* ===================== DETALLES DE INSTALACIÓN ===================== */}
      <CustomTabPanel index={valueTab} value={2}>
        <OTDetalleInstallTecnicoFormTab
          form={form as any}
          errors={errors}
          shrink={true}
          coords={ordentrabajo?.agendamiento_data?.coordenadas || ''}
          ordenTrabajo={ordentrabajo!}
          isOnlyView
        />
      </CustomTabPanel>

      {/* ===================== DETALLES CLIENTE ===================== */}
      <CustomTabPanel index={valueTab} value={3}>
        <OTDetalleClienteInstallTecnicoFormTab
          form={form as any}
          errors={errors}
          shrink={true}
          // isOnlyView
        />
      </CustomTabPanel>

      {/* ===================== MATERIALES =================== */}
      <CustomTabPanel index={valueTab} value={4}>
        <EquiposMaterialesViewOTInstallFormTab ordenTrabajo={ordentrabajo!} />
      </CustomTabPanel>

      {/* ===================== DOCUMENTOS =================== */}
      <CustomTabPanel index={valueTab} value={5}>
        <AuditoriaOTInstallDocsFormTab ordenTrabajo={ordentrabajo!} />
      </CustomTabPanel>
      {/* ===================== ENVIAR A ACTUALIZAR POR TÉCNICO =================== */}
      <RejectToUpdateInstalacionAsigTecModal
        open={openRejectModal}
        setOpen={setOpenRejectModal}
        idOrdenTrabajo={ordentrabajo?.id_orden_trabajo!}
        ordenTrabajo={ordentrabajo!}
      />
    </TabsFormBoxScene>
  );
};

export default SaveAuditoriaInstallOT;
